import * as iots from "io-ts";
import type { BuilderAction, BuilderActionPerApp } from "@glide/app-description";
import type { BuilderActionWithExplicitPerApp } from "@glide/generator/dist/js/Database/builder-actions";
import { assertNever } from "@glideapps/ts-necessities";

export enum BuilderActionMutationKind {
    // This mutation currently only exists to make the optimistic mutation
    // manager also able to create builder actions, vs just updating them.
    CreateBuilderAction = "CREATE_BUILDER_ACTION",
    UpdateBuilderAction = "UPDATE_BUILDER_ACTION",
}

export const createBuilderActionMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.CreateBuilderAction),
    data: iots.type({
        actionID: iots.string,
        value: iots.intersection([
            iots.type({
                action: iots.unknown as iots.Type<BuilderAction["action"], unknown, unknown>,
                perApp: iots.unknown as iots.Type<BuilderActionPerApp, unknown, unknown>,
            }),
            iots.partial({
                title: iots.string,
                description: iots.string,
            }),
        ]),
    }),
});

export const updateBuilderActionMetaMutationCodec = iots.type({
    kind: iots.literal(BuilderActionMutationKind.UpdateBuilderAction),
    data: iots.type({
        actionID: iots.string,
        updates: iots.partial({
            title: iots.string,
            description: iots.string,
            action: iots.unknown as iots.Type<BuilderAction["action"], unknown, unknown>,
            perApp: iots.unknown as iots.Type<BuilderActionPerApp, unknown, unknown>,
        }),
    }),
});

export type BuilderActionMutation = iots.TypeOf<
    typeof createBuilderActionMetaMutationCodec | typeof updateBuilderActionMetaMutationCodec
>;

export function applyBuilderActionMutation(
    builderAction: BuilderActionWithExplicitPerApp,
    { kind, data }: BuilderActionMutation
): BuilderActionWithExplicitPerApp {
    switch (kind) {
        case BuilderActionMutationKind.CreateBuilderAction:
            // This is a bit weird
            return data.value;

        case BuilderActionMutationKind.UpdateBuilderAction: {
            const { updates } = data;
            return {
                ...builderAction,
                title: updates.title ?? builderAction.title,
                description: updates.description ?? builderAction.description,
                action: updates.action ?? builderAction.action,
                perApp: updates.perApp ?? builderAction.perApp,
            };
        }

        default:
            return assertNever(kind);
    }
}
